/**
 * Centralized page cleanup — called on every route change to tear down
 * the previous screen's state, timers, and subscriptions.
 *
 * Replaces the old window-global `clearPageGlobalVarsAndCloseDialogs()` with
 * proper ES module imports.
 */

// Services
import { cleanUpCallCard, setCallCardScreenLoaded, setCallCardRealtimeEnabled } from '@/services/cad-service';
import { cleanUpCaseReport } from '@/services/case-report-service';
import { cleanUpMap, clearMapRefresh, setMapScreenLoaded } from '@/services/map-service';
import { api } from '@/services/api-class';

// Screen cleanup functions
import { cleanUpInProgressScreen } from '@/components/screens/InProgressScreen';
import { cleanUpOfficerStatus } from '@/components/screens/OfficerStatusScreen';
import { clearLogPageRefresh } from '@/components/screens/LogScreen';
import { destroyNotesScreen } from '@/components/screens/NotesScreen';
import { chatCleanup } from '@/components/screens/MessagingScreen';
import { resetCurrentGuardTourStop } from '@/components/screens/GuardToursScreen';
import { destroyPaymentManagementScreen } from '@/components/screens/PaymentManagementScreen';

// Utils / models
import { closeNotificationModals } from '@/utils/call-card-helpers';
import { clearCallCardClockRefresh } from '@/models/legacy/index';
import { stopPanicSounds } from '@/models/legacy/notification-dialogs';

/**
 * Tears down all screen-specific state when navigating away from a page.
 * Called by RouteHandler on every route change.
 */
export function clearPageGlobalVarsAndCloseDialogs(): void {
  setCallCardScreenLoaded(false);
  setCallCardRealtimeEnabled(false);
  cleanUpCallCard();
  cleanUpCaseReport();
  cleanUpInProgressScreen();
  setMapScreenLoaded(false);
  cleanUpMap();
  clearMapRefresh();
  cleanUpOfficerStatus();
  clearCallCardClockRefresh();
  clearLogPageRefresh();
  destroyNotesScreen();
  chatCleanup();
  resetCurrentGuardTourStop();
  closeNotificationModals();
  // clearActivityReportLastResults is a no-op (React handles cleanup)
  destroyPaymentManagementScreen();
  stopPanicSounds();
  api.clearDuplicateCallCheck();
}
